import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Lock, Mail, Shield, Monitor, AlertTriangle, UserX, Link2, Github, Smartphone, QrCode, CheckCircle2, XCircle } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { DeleteAccountDialog } from "@/components/settings/DeleteAccountDialog";
import { z } from "zod";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const passwordSchema = z.object({
  currentPassword: z.string().min(1, "Aktuelles Passwort ist erforderlich"),
  newPassword: z.string()
    .min(8, "Passwort muss mindestens 8 Zeichen lang sein")
    .regex(/[A-Z]/, "Passwort muss mindestens einen Großbuchstaben enthalten")
    .regex(/[a-z]/, "Passwort muss mindestens einen Kleinbuchstaben enthalten")
    .regex(/[0-9]/, "Passwort muss mindestens eine Zahl enthalten"),
  confirmPassword: z.string().min(1, "Passwort-Bestätigung ist erforderlich"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwörter stimmen nicht überein",
  path: ["confirmPassword"],
});

const emailSchema = z.object({
  newEmail: z.string().email("Ungültige E-Mail-Adresse"),
});

const Security = () => {
  const { user, session, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});

  // Email change state
  const [newEmail, setNewEmail] = useState("");
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [emailError, setEmailError] = useState("");

  // Account deletion state
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  // MFA state
  const [mfaFactors, setMfaFactors] = useState<any[]>([]);
  const [isLoadingMfa, setIsLoadingMfa] = useState(true);
  const [isEnrollingMfa, setIsEnrollingMfa] = useState(false);
  const [mfaEnrollData, setMfaEnrollData] = useState<{ qr: string; secret: string; factorId: string } | null>(null);
  const [mfaVerifyCode, setMfaVerifyCode] = useState("");
  const [isVerifyingMfa, setIsVerifyingMfa] = useState(false);
  const [showMfaEnrollDialog, setShowMfaEnrollDialog] = useState(false);
  const [isUnenrollingMfa, setIsUnenrollingMfa] = useState(false);

  // Load MFA factors on mount
  useEffect(() => {
    const loadMfaFactors = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase.auth.mfa.listFactors();
        if (error) throw error;
        setMfaFactors(data?.totp || []);
      } catch (error) {
        console.error("Error loading MFA factors:", error);
      } finally {
        setIsLoadingMfa(false);
      }
    };
    
    loadMfaFactors();
  }, [user]);

  // Session info
  const currentDevice = session ? {
    lastSignIn: new Date(session.user.last_sign_in_at || Date.now()).toLocaleString('de-DE'),
    expiresAt: session.expires_at ? new Date(session.expires_at * 1000).toLocaleString('de-DE') : 'N/A',
  } : null;

  // MFA handlers
  const handleEnrollMfa = async () => {
    setIsEnrollingMfa(true);
    
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Authenticator App',
      });
      
      if (error) throw error;
      
      if (data) {
        setMfaEnrollData({
          qr: data.totp.qr_code,
          secret: data.totp.secret,
          factorId: data.id,
        });
        setShowMfaEnrollDialog(true);
      }
    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error.message || "MFA konnte nicht aktiviert werden.",
        variant: "destructive",
      });
    } finally {
      setIsEnrollingMfa(false);
    }
  };

  const handleVerifyMfa = async () => {
    if (!mfaEnrollData || mfaVerifyCode.length !== 6) return;
    
    setIsVerifyingMfa(true);
    
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: mfaEnrollData.factorId,
      });
      
      if (challengeError) throw challengeError;
      
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: mfaEnrollData.factorId,
        challengeId: challengeData.id,
        code: mfaVerifyCode,
      });
      
      if (verifyError) throw verifyError;
      
      // Reload factors
      const { data: factorsData } = await supabase.auth.mfa.listFactors();
      setMfaFactors(factorsData?.totp || []);
      
      setShowMfaEnrollDialog(false);
      setMfaEnrollData(null);
      setMfaVerifyCode("");
      
      toast({
        title: "MFA aktiviert",
        description: "Zwei-Faktor-Authentifizierung wurde erfolgreich eingerichtet.",
      });
    } catch (error: any) {
      toast({
        title: "Verifizierung fehlgeschlagen",
        description: error.message || "Der eingegebene Code ist ungültig.",
        variant: "destructive",
      });
    } finally {
      setIsVerifyingMfa(false);
    }
  };

  const handleUnenrollMfa = async (factorId: string) => {
    setIsUnenrollingMfa(true);
    
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw error;
      
      // Reload factors
      const { data: factorsData } = await supabase.auth.mfa.listFactors();
      setMfaFactors(factorsData?.totp || []);
      
      toast({
        title: "MFA deaktiviert",
        description: "Zwei-Faktor-Authentifizierung wurde entfernt.",
      });
    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error.message || "MFA konnte nicht deaktiviert werden.",
        variant: "destructive",
      });
    } finally {
      setIsUnenrollingMfa(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordErrors({});

    // Validate input
    try {
      passwordSchema.parse({ currentPassword, newPassword, confirmPassword });
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            errors[err.path[0].toString()] = err.message;
          }
        });
        setPasswordErrors(errors);
        return;
      }
    }

    setIsChangingPassword(true);

    try {
      // Supabase doesn't require current password for update, but we verify user is authenticated
      if (!session) {
        throw new Error("Keine aktive Sitzung gefunden");
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      // Clear form
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");

      toast({
        title: "Passwort geändert",
        description: "Dein Passwort wurde erfolgreich aktualisiert.",
      });
    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error.message || "Passwort konnte nicht geändert werden.",
        variant: "destructive",
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError("");

    // Validate input
    try {
      emailSchema.parse({ newEmail });
    } catch (error) {
      if (error instanceof z.ZodError) {
        setEmailError(error.errors[0]?.message || "Ungültige E-Mail");
        return;
      }
    }

    if (newEmail === user?.email) {
      setEmailError("Die neue E-Mail ist identisch mit der aktuellen");
      return;
    }

    setIsChangingEmail(true);

    try {
      const { error } = await supabase.auth.updateUser(
        { email: newEmail },
      );

      if (error) throw error;

      setNewEmail("");

      toast({
        title: "Bestätigungs-E-Mail gesendet",
        description: "Bitte prüfe deine neue E-Mail-Adresse und bestätige die Änderung.",
      });
    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error.message || "E-Mail konnte nicht geändert werden.",
        variant: "destructive",
      });
    } finally {
      setIsChangingEmail(false);
    }
  };

  const handleExportData = async () => {
    if (!user) return;

    try {
      // Fetch all user data
      const [profileData, sessionsData, workspacesData] = await Promise.all([
        supabase.from("profiles").select("*").eq("id", user.id).single(),
        supabase.from("decision_sessions").select("*").eq("created_by", user.id),
        supabase.from("workspace_members").select("*, workspaces(*)").eq("user_id", user.id),
      ]);

      const exportData = {
        export_date: new Date().toISOString(),
        user_id: user.id,
        profile: profileData.data,
        sessions: sessionsData.data || [],
        workspaces: workspacesData.data || [],
      };

      // Create and download JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `quentari-daten-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Export erfolgreich",
        description: "Ihre Daten wurden heruntergeladen.",
      });
    } catch (error: any) {
      toast({
        title: "Fehler beim Export",
        description: error.message || "Daten konnten nicht exportiert werden.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleDeleteAccount = async () => {
    if (!user || !session) return;

    setIsDeletingAccount(true);

    try {
      // Call the Edge Function to delete the account securely
      const { data, error } = await supabase.functions.invoke('delete-account', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message || 'Account konnte nicht gelöscht werden.');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      toast({
        title: "Account gelöscht",
        description: data?.message || "Ihr Account wurde erfolgreich gelöscht.",
      });

      // Sign out the user
      await supabase.auth.signOut();
      navigate("/");
    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error.message || "Account konnte nicht gelöscht werden.",
        variant: "destructive",
      });
      setIsDeletingAccount(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return null; // ProtectedRoute handles redirect
  }

  return (
    <div className="max-w-4xl space-y-6 pb-20 md:pb-6">
      <Alert>
        <Shield className="h-4 w-4" />
        <AlertTitle>Sicherheit</AlertTitle>
        <AlertDescription>
          Verwalte deine Anmeldedaten und Sicherheitseinstellungen. Änderungen können eine erneute Anmeldung erfordern.
        </AlertDescription>
      </Alert>

      {/* Password Change */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-primary" />
            <CardTitle>Passwort ändern</CardTitle>
          </div>
          <CardDescription>
            Aktualisiere dein Passwort. Verwende ein starkes Passwort mit mindestens 8 Zeichen.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Aktuelles Passwort</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={isChangingPassword}
                placeholder="••••••••"
              />
              {passwordErrors.currentPassword && (
                <p className="text-sm text-destructive">{passwordErrors.currentPassword}</p>
              )}
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="new-password">Neues Passwort</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isChangingPassword}
                placeholder="••••••••"
              />
              {passwordErrors.newPassword && (
                <p className="text-sm text-destructive">{passwordErrors.newPassword}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Mindestens 8 Zeichen, mit Groß-/Kleinbuchstaben und Zahlen
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Neues Passwort bestätigen</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isChangingPassword}
                placeholder="••••••••"
              />
              {passwordErrors.confirmPassword && (
                <p className="text-sm text-destructive">{passwordErrors.confirmPassword}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={isChangingPassword}
              className="w-full sm:w-auto"
            >
              {isChangingPassword && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Passwort ändern
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Email Change */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            <CardTitle>E-Mail-Adresse ändern</CardTitle>
          </div>
          <CardDescription>
            Aktualisiere deine E-Mail-Adresse. Du erhältst eine Bestätigungs-E-Mail an die neue Adresse.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleEmailChange} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-email">Aktuelle E-Mail</Label>
              <Input
                id="current-email"
                type="email"
                value={user.email || ""}
                disabled
                className="bg-muted"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-email">Neue E-Mail-Adresse</Label>
              <Input
                id="new-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                disabled={isChangingEmail}
                placeholder="neue@email.de"
              />
              {emailError && (
                <p className="text-sm text-destructive">{emailError}</p>
              )}
            </div>

            <Alert variant="default">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                Nach der Änderung musst du die neue E-Mail-Adresse bestätigen. Du wirst abgemeldet und musst dich erneut anmelden.
              </AlertDescription>
            </Alert>

            <Button
              type="submit"
              disabled={isChangingEmail || !newEmail}
              className="w-full sm:w-auto"
            >
              {isChangingEmail && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              E-Mail-Adresse ändern
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Active Sessions */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Monitor className="h-5 w-5 text-primary" />
            <CardTitle>Aktive Sitzung</CardTitle>
          </div>
          <CardDescription>
            Informationen über deine aktuelle Anmeldesitzung
          </CardDescription>
        </CardHeader>
        <CardContent>
          {currentDevice ? (
            <div className="space-y-4">
              <div className="flex items-start justify-between p-4 border rounded-lg bg-muted/30">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Monitor className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Aktuelles Gerät</span>
                  </div>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>Letzte Anmeldung: {currentDevice.lastSignIn}</p>
                    <p>Sitzung läuft ab: {currentDevice.expiresAt}</p>
                    <p className="text-xs pt-2 text-primary">● Aktiv</p>
                  </div>
                </div>
              </div>

              <Alert>
                <AlertDescription className="text-sm">
                  Supabase verwaltet Sessions automatisch. Wenn du verdächtige Aktivitäten feststellst, ändere sofort dein Passwort.
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Keine aktive Sitzung gefunden.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Connected Accounts */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-primary" />
            <CardTitle>Verbundene Konten</CardTitle>
          </div>
          <CardDescription>
            Verknüpfe deinen Account mit OAuth-Providern für vereinfachte Anmeldung
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Google */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-muted">
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <div>
                  <p className="font-medium">Google</p>
                  <p className="text-sm text-muted-foreground">
                    {user?.identities?.some((id) => id.provider === 'google') 
                      ? 'Verbunden' 
                      : 'Nicht verbunden'}
                  </p>
                </div>
              </div>
              {user?.identities?.some((id) => id.provider === 'google') ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    const identity = user.identities?.find((id) => id.provider === 'google');
                    if (identity) {
                      try {
                        await supabase.auth.unlinkIdentity(identity);
                        toast({
                          title: "Verbindung getrennt",
                          description: "Google-Account wurde erfolgreich getrennt.",
                        });
                      } catch (error: any) {
                        toast({
                          title: "Fehler",
                          description: error.message || "Verbindung konnte nicht getrennt werden.",
                          variant: "destructive",
                        });
                      }
                    }
                  }}
                >
                  Trennen
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      await supabase.auth.linkIdentity({ provider: 'google' });
                    } catch (error: any) {
                      toast({
                        title: "Fehler",
                        description: error.message || "Verbindung konnte nicht hergestellt werden.",
                        variant: "destructive",
                      });
                    }
                  }}
                >
                  Verbinden
                </Button>
              )}
            </div>

            {/* GitHub */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-muted">
                  <Github className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium">GitHub</p>
                  <p className="text-sm text-muted-foreground">
                    {user?.identities?.some((id) => id.provider === 'github') 
                      ? 'Verbunden' 
                      : 'Nicht verbunden'}
                  </p>
                </div>
              </div>
              {user?.identities?.some((id) => id.provider === 'github') ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    const identity = user.identities?.find((id) => id.provider === 'github');
                    if (identity) {
                      try {
                        await supabase.auth.unlinkIdentity(identity);
                        toast({
                          title: "Verbindung getrennt",
                          description: "GitHub-Account wurde erfolgreich getrennt.",
                        });
                      } catch (error: any) {
                        toast({
                          title: "Fehler",
                          description: error.message || "Verbindung konnte nicht getrennt werden.",
                          variant: "destructive",
                        });
                      }
                    }
                  }}
                >
                  Trennen
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      await supabase.auth.linkIdentity({ provider: 'github' });
                    } catch (error: any) {
                      toast({
                        title: "Fehler",
                        description: error.message || "Verbindung konnte nicht hergestellt werden.",
                        variant: "destructive",
                      });
                    }
                  }}
                >
                  Verbinden
                </Button>
              )}
            </div>

            {/* Microsoft */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-muted">
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zm12.6 0H12.6V0H24v11.4z"/>
                  </svg>
                </div>
                <div>
                  <p className="font-medium">Microsoft</p>
                  <p className="text-sm text-muted-foreground">
                    {user?.identities?.some((id) => id.provider === 'azure') 
                      ? 'Verbunden' 
                      : 'Nicht verbunden'}
                  </p>
                </div>
              </div>
              {user?.identities?.some((id) => id.provider === 'azure') ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    const identity = user.identities?.find((id) => id.provider === 'azure');
                    if (identity) {
                      try {
                        await supabase.auth.unlinkIdentity(identity);
                        toast({
                          title: "Verbindung getrennt",
                          description: "Microsoft-Account wurde erfolgreich getrennt.",
                        });
                      } catch (error: any) {
                        toast({
                          title: "Fehler",
                          description: error.message || "Verbindung konnte nicht getrennt werden.",
                          variant: "destructive",
                        });
                      }
                    }
                  }}
                >
                  Trennen
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      await supabase.auth.linkIdentity({ provider: 'azure' });
                    } catch (error: any) {
                      toast({
                        title: "Fehler",
                        description: error.message || "Verbindung konnte nicht hergestellt werden.",
                        variant: "destructive",
                      });
                    }
                  }}
                >
                  Verbinden
                </Button>
              )}
            </div>

            <Alert>
              <AlertDescription className="text-sm">
                Verbundene Konten ermöglichen es dir, dich schneller anzumelden. Du kannst mehrere Provider gleichzeitig verbinden.
              </AlertDescription>
            </Alert>
          </div>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-primary" />
            <CardTitle>Zwei-Faktor-Authentifizierung</CardTitle>
          </div>
          <CardDescription>
            Schütze deinen Account mit einer zusätzlichen Sicherheitsebene
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingMfa ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : mfaFactors.length > 0 ? (
            <div className="space-y-4">
              {mfaFactors.map((factor) => (
                <div key={factor.id} className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{factor.friendly_name || 'Authenticator App'}</p>
                      <p className="text-sm text-muted-foreground">
                        Aktiviert am {new Date(factor.created_at).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleUnenrollMfa(factor.id)}
                    disabled={isUnenrollingMfa}
                  >
                    {isUnenrollingMfa ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Deaktivieren"
                    )}
                  </Button>
                </div>
              ))}
              
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  Zwei-Faktor-Authentifizierung ist aktiv. Bei jeder Anmeldung wird ein Code von deiner Authenticator-App benötigt.
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg border-dashed">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-muted">
                    <XCircle className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium">Nicht aktiviert</p>
                    <p className="text-sm text-muted-foreground">
                      Aktiviere 2FA für zusätzliche Sicherheit
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleEnrollMfa}
                  disabled={isEnrollingMfa}
                >
                  {isEnrollingMfa ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <QrCode className="mr-2 h-4 w-4" />
                  )}
                  Aktivieren
                </Button>
              </div>
              
              <Alert>
                <AlertDescription className="text-sm">
                  Verwenden Sie eine Authenticator-App wie Google Authenticator, Microsoft Authenticator oder Authy, um 2FA einzurichten.
                </AlertDescription>
              </Alert>
            </div>
          )}
        </CardContent>
      </Card>

      {/* MFA Enrollment Dialog */}
      <Dialog open={showMfaEnrollDialog} onOpenChange={setShowMfaEnrollDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              2FA einrichten
            </DialogTitle>
            <DialogDescription>
              Scanne den QR-Code mit deiner Authenticator-App und gib den generierten Code ein.
            </DialogDescription>
          </DialogHeader>
          
          {mfaEnrollData && (
            <div className="space-y-6 py-4">
              {/* QR Code */}
              <div className="flex justify-center">
                <div className="p-4 bg-white rounded-lg">
                  <img 
                    src={mfaEnrollData.qr} 
                    alt="QR Code für 2FA" 
                    className="w-48 h-48"
                  />
                </div>
              </div>
              
              {/* Manual Secret */}
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">
                  Oder gib diesen Code manuell ein:
                </Label>
                <code className="block p-3 bg-muted rounded-lg text-center font-mono text-sm break-all">
                  {mfaEnrollData.secret}
                </code>
              </div>
              
              <Separator />
              
              {/* Verification Code Input */}
              <div className="space-y-3">
                <Label>Bestätigungscode eingeben</Label>
                <div className="flex justify-center">
                  <InputOTP 
                    maxLength={6} 
                    value={mfaVerifyCode}
                    onChange={setMfaVerifyCode}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowMfaEnrollDialog(false);
                setMfaEnrollData(null);
                setMfaVerifyCode("");
              }}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleVerifyMfa}
              disabled={isVerifyingMfa || mfaVerifyCode.length !== 6}
            >
              {isVerifyingMfa && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Bestätigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Section */}
      <Card className="border-destructive">
        <CardHeader>
          <div className="flex items-center gap-2">
            <UserX className="h-5 w-5 text-destructive" />
            <CardTitle className="text-destructive">Gefährliche Zone</CardTitle>
          </div>
          <CardDescription>
            Account dauerhaft löschen. Diese Aktion kann nicht rückgängig gemacht werden.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Warnung</AlertTitle>
            <AlertDescription>
              Das Löschen deines Accounts entfernt alle deine Daten permanent aus unserer Datenbank.
              Dies umfasst dein Profil, alle Entscheidungs-Sessions, Team-Mitgliedschaften und hochgeladene Dateien.
            </AlertDescription>
          </Alert>

          <div className="flex flex-col sm:flex-row gap-4 pt-2">
            <DeleteAccountDialog
              onConfirm={handleDeleteAccount}
              onExportData={handleExportData}
              isDeleting={isDeletingAccount}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Security;
